import React, { useEffect } from 'react'
import { AppProps } from '../App'
import { useLocation, useNavigate } from 'react-router-dom'
import { useEventListener } from '../utils/functions';
import { Logo } from '../utils/svgs';
import { appName, storageKeys } from '../utils/constants';
import { HiOutlineHome } from "react-icons/hi";
import { TbCreditCard, TbSettings } from "react-icons/tb";
import { HiBars3 } from "react-icons/hi2";
import { AiOutlineClose } from "react-icons/ai";
import { useTranslation } from 'react-i18next';
import SidebarContent from './SidebarContent';

export type MainType = 'settings' | 'project' | 'docs';

const Navbar = (props: AppProps) => {

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [main, setMain] = React.useState<'' | MainType>('');
  const [showMenu, setShowMenu] = React.useState(false);
  const [showSidebar, setShowSidebar] = React.useState(false);
  const [showNav, setShowNav] = React.useState(false);

  useEffect(() => {
    // set main for sidebar options
    const mainString = location.pathname.split('/')[1];
    if (mainString === 'settings' || mainString === 'project' || mainString === 'docs') {
      setMain(mainString as MainType);
    } else {
      setMain('');
    }
  }, [location, props]);

  // change title when location changes
  useEffect(() => {
    
  }, [location.pathname]);

  useEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (!target.closest('.user-avatar') && !target.closest('.app-menu')) {
      setShowMenu(false);
    }
    if (!target.closest('.sidebar-trigger') && !target.closest('.app-sidebar')) {
      setShowSidebar(false);
    }
    if (!target.closest('.nav') && !target.closest('.nav-button')) {
      setShowNav(false);
    }
  })

  // go to given route
  const goto = (path: string) => {
    setShowMenu(false);
    navigate(path);
  }

  // sign out
  const signOut = async () => {
    props.setUser(null);
    localStorage.removeItem(storageKeys.userId);
    localStorage.removeItem(storageKeys.userId2);
    navigate('/');
  }

  return (
    <>
      <header className='sticky top-0 z-30 bg-white border-b p-2 px-4 md:p-4 md:px-8 flex items-center justify-between'>
        {/* logo */}
        {main ? <button onClick={() => setTimeout(() => setShowSidebar(!showSidebar), 10)} className='sidebar-trigger select-none md:hidden h-8 w-8 rounded-md hover:bg-black/5 flex items-center justify-center border mr-4'>{showSidebar ? <AiOutlineClose className='text-lg' /> : <HiBars3 className='text-lg' />}</button>
          : <div onClick={() => navigate('/')} className='md:hidden w-6 h-6 cursor-pointer'><Logo /></div>}
        <div onClick={() => navigate('/')} className='flex items-center cursor-pointer'>
          <div className='hidden md:flex w-6 h-6 md:w-7 md:h-7'><Logo /></div>
          <div className='app-name ml-2 hidden md:flex font-semibold text-xl text-primary'>{appName}</div>
        </div>
        {props.user ? <div className='flex items-center'>
          <button onClick={() => goto('/docs')} className='px-4 text-sm hover:text-primary mr-4'>{t('menu.docs')}</button>
          <div className='relative'>
            {/* avatar */}
            <div onClick={() => setShowMenu(!showMenu)} className={`user-avatar overflow-hidden select-none text-white cursor-pointer text-xs md:text-sm rounded-full w-8 h-8 flex items-center justify-center ${props.userImage ? '' : 'bg-black'}`}>
              {props.userImage ? <img src={props.userImage} className='w-full h-full object-cover' alt='' /> : props.user.name[0]}
            </div>
            {/* menu */}
            {showMenu && <div className='app-menu z-10 flex flex-col bg-white p-2 rounded-xl absolute top-full mt-2 md:mt-3 -right-4 md:right-0 w-screen h-menu md:h-auto md:max-h-menu overflow-auto md:max-w-64 border' style={{ boxShadow: '0px 0px 8px 2px #0001' }}>
              <div className='*:whitespace-nowrap *:overflow-hidden *:text-ellipsis p-2 px-4'>
                <div className='font-bold'>{props.user.name}</div>
                <div className='text-xs font-light text-black/40'>{props.user.email}</div>
              </div>
              <button onClick={() => goto('/')} className='text-sm p-2 px-4 flex items-center hover:bg-primary/10 rounded-lg'><HiOutlineHome className='mr-2' />{t('menu.home')}</button>
              <button onClick={() => goto('/settings')} className='text-sm p-2 px-4 flex items-center hover:bg-primary/10 rounded-lg mt-1'><TbSettings className='mr-2' />{t('menu.settings')}</button>
              <button onClick={() => goto('/pricing')} className='text-sm p-2 px-4 flex items-center hover:bg-primary/10 rounded-lg mt-1'><TbCreditCard className='mr-2' />{t('menu.pricing')}</button>
              <div className='m-2 border-t' />
              <button onClick={signOut} className='text-sm p-2 px-4 flex items-center text-red-400 hover:bg-primary/10 rounded-lg mt-1'>{t('auth.sign-out')}</button>
            </div>}
          </div>
        </div> : <>
          <nav className='hidden md:flex items-center gap-4'>          
            <button onClick={() => goto('/pricing')} className='px-2 text-sm font-light hover:text-primary'>{t('menu.pricing')}</button>
            <button onClick={() => goto('/docs')} className='px-2 text-sm font-light hover:text-primary mr-2'>{t('menu.docs')}</button>
            <button onClick={() => navigate('/signin')} className='text-sm text-white bg-primary py-1.5 px-4 rounded-lg'>{t('auth.sign-in')}</button>
          </nav>
          {/* mobile nav */}
          <div className='relative md:hidden'>
            <button onClick={() => setTimeout(() => setShowNav(!showNav), 10)} className='nav-button select-none p-1 px-2 rounded-md flex items-center justify-center'>{showNav ? <AiOutlineClose className='text-2xl' /> : <HiBars3 className='text-2xl' />}</button>
            <nav className={`nav ${showNav ? 'open' : 'hidden'} fixed animate z-30 flex flex-col bg-white w-screen left-0 p-4 px-8 h-menu bottom-0`}>
              <button onClick={() => goto('/')} className='scale-none border-b p-4 text-gray-600 text-sm text-start'>{t('menu.home')}</button>
              <button onClick={() => goto('/pricing')} className='scale-none border-b p-4 text-gray-600 text-sm text-start'>{t('menu.pricing')}</button>
              <button onClick={() => goto('/docs')} className='scale-none border-b p-4 text-gray-600 text-sm text-start'>{t('menu.docs')}</button>
              <button onClick={() => navigate('/signin')} className='scale-none border-b p-4 text-gray-600 text-sm text-start'>{t('auth.sign-in')}</button>
            </nav>
          </div>
        </>}
      </header>

      {/* mobile ai tools */}
      {/* <div className='ai-box animate z-20 fixed border w-full max-w-100 rounded-xl flex flex-col overflow-auto bg-white h-menu md:h-menu-md bottom-0' style={{ right: props.isAIOpened ? 0 : '-110%', boxShadow: '0px 0px 8px 2px #0001' }}>
        <AITools {...props} />
      </div> */}

      {/* mobile sidebar */}
      <aside className='app-sidebar animate z-20 fixed md:hidden w-full max-w-64 p-2 gap-1 flex flex-col border rounded-xl overflow-auto bg-white h-menu bottom-0' style={{ left: showSidebar ? 0 : '-110%', boxShadow: '0px 0px 8px 2px #0001' }}>
        <SidebarContent setShowSidebar={setShowSidebar} {...props} />
      </aside>

    </>
  )
}

export default Navbar